import React, { createContext, useContext, useEffect, useState, useMemo } from "react";
import { toast } from "sonner";

const StoreContext = createContext(null);
export const useStore = () => useContext(StoreContext);

const load = (k, def) => {
  try {
    const v = localStorage.getItem(k);
    return v ? JSON.parse(v) : def;
  } catch {
    return def;
  }
};

export const StoreProvider = ({ children }) => {
  const [cart, setCart] = useState(() => load("sonia_cart", []));
  const [wishlist, setWishlist] = useState(() => load("sonia_wishlist", []));
  const [cartOpen, setCartOpen] = useState(false);
  const [coupon, setCoupon] = useState(() => load("sonia_coupon", null));

  useEffect(() => localStorage.setItem("sonia_cart", JSON.stringify(cart)), [cart]);
  useEffect(() => localStorage.setItem("sonia_wishlist", JSON.stringify(wishlist)), [wishlist]);
  useEffect(() => localStorage.setItem("sonia_coupon", JSON.stringify(coupon)), [coupon]);

  const keyOf = (slug, wl) => `${slug}__${wl}`;

  const addToCart = (product, weight, qty = 1, silent = false) => {
    const k = keyOf(product.slug, weight.label);
    setCart((prev) => {
      const exists = prev.find((i) => i.key === k);
      if (exists) {
        return prev.map((i) => (i.key === k ? { ...i, qty: Math.min(99, i.qty + qty) } : i));
      }
      return [
        ...prev,
        {
          key: k,
          slug: product.slug,
          name: product.name,
          image: product.image,
          weight_label: weight.label,
          price: weight.price,
          mrp: weight.mrp,
          qty,
        },
      ];
    });
    if (!silent) toast.success(`${product.name} (${weight.label}) added to cart`);
  };

  const updateQty = (key, qty) => {
    if (qty <= 0) return removeFromCart(key);
    setCart((prev) => prev.map((i) => (i.key === key ? { ...i, qty: Math.min(99, qty) } : i)));
  };

  const removeFromCart = (key) => setCart((prev) => prev.filter((i) => i.key !== key));
  const clearCart = () => {
    setCart([]);
    setCoupon(null);
  };

  const toggleWishlist = (slug) => {
    setWishlist((prev) => {
      if (prev.includes(slug)) {
        toast("Removed from wishlist");
        return prev.filter((s) => s !== slug);
      }
      toast.success("Saved to wishlist");
      return [...prev, slug];
    });
  };
  const isWished = (slug) => wishlist.includes(slug);

  const cartCount = useMemo(() => cart.reduce((a, i) => a + i.qty, 0), [cart]);
  const subtotal = useMemo(() => cart.reduce((a, i) => a + i.price * i.qty, 0), [cart]);
  const mrpTotal = useMemo(() => cart.reduce((a, i) => a + i.mrp * i.qty, 0), [cart]);
  const discount = useMemo(() => (coupon ? coupon.discount : 0), [coupon]);
  const afterDiscount = Math.max(0, subtotal - discount);
  const shipping = afterDiscount >= 1500 || cart.length === 0 ? 0 : 60;
  const total = afterDiscount + shipping;

  const value = {
    cart, wishlist, cartOpen, setCartOpen, coupon, setCoupon,
    addToCart, updateQty, removeFromCart, clearCart, toggleWishlist, isWished,
    cartCount, subtotal, mrpTotal, discount, shipping, total,
  };
  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
};
